import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ItemDto, ItemStubDto, PlaylistCardDto } from '../api/types';
import { hostOf, itemsWord, relativeTime, typeLabel } from '../lib/format';
import { IconExternal, IconLayers } from './icons';
import { Avatar, VisibilityBadge } from './ui';

export function Cover({ src, title, className = '', eager = false }: {
  src: string | null;
  title: string;
  className?: string;
  eager?: boolean;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  useEffect(() => setFailedSrc(null), [src]);
  const hasImage = Boolean(src && failedSrc !== src);
  return (
    <div className={`cover ${className}${hasImage ? '' : ' cover--missing'}`}>
      <span className="cover__fallback" aria-hidden="true">
        <span className="cover__letter">{title.trim().slice(0, 1).toUpperCase() || 'm'}</span>
        <span className="cover__title clamp-3">{title}</span>
        <span className="cover__caption">my-lib / collection</span>
      </span>
      {hasImage ? (
        <img
          key={src}
          src={src!}
          alt=""
          className={loadedSrc === src ? 'cover__image--loaded' : undefined}
          loading={eager ? 'eager' : 'lazy'}
          onLoad={() => setLoadedSrc(src)}
          onError={() => setFailedSrc(src)}
        />
      ) : null}
    </div>
  );
}

interface ItemCardProps {
  item: ItemDto | ItemStubDto;
  to: string;
  external?: boolean;
  trailing?: React.ReactNode;
  variant?: 'shelf' | 'route';
}

export function ItemCard({ item, to, external = false, trailing, variant = 'shelf' }: ItemCardProps) {
  const location = useLocation();
  const portrait = ['book', 'movie', 'series', 'game'].includes(item.type);
  const className = `item-card item-card--${variant} item-card--${item.type}`;
  const body = (
    <>
      <Cover src={item.imageUrl} title={item.title} className={`item-card__cover${portrait ? ' cover--portrait' : ''}`} />
      <div className="item-card__body">
        <span className="item-card__category">{typeLabel(item.type)}</span>
        <p className="item-card__title clamp-2">{item.title}</p>
        {variant === 'route' && item.description ? <p className="item-card__desc clamp-3">{item.description}</p> : null}
        <div className="item-card__foot">
          <span className="item-card__source clamp-1">{item.sourceName || item.sourceDomain || hostOf(item.url)}</span>
          {trailing ?? (external ? <IconExternal size={15} /> : null)}
        </div>
      </div>
    </>
  );
  return external ? (
    <a className={className} href={to} target="_blank" rel="noreferrer noopener">{body}</a>
  ) : (
    <Link className={className} to={to} state={{ from: `${location.pathname}${location.search}` }}>{body}</Link>
  );
}

export function PlaylistCard({ playlist, to }: { playlist: PlaylistCardDto; to?: string }) {
  return (
    <Link className="playlist-card" to={to ?? `/p/${playlist.id}`}>
      <div className="playlist-card__jacket">
        <Cover src={playlist.coverImageUrl} title={playlist.title} className="playlist-card__cover" />
        <span className="playlist-card__count"><IconLayers size={14} /> {itemsWord(playlist.itemCount)}</span>
        <span className="playlist-card__open" aria-hidden="true">↗</span>
      </div>
      <div className="playlist-card__body">
        <div className="playlist-card__meta">
          <VisibilityBadge visibility={playlist.visibility} />
          <span className="tiny muted">{relativeTime(playlist.updatedAt)}</span>
        </div>
        <h3 className="playlist-card__title clamp-2">{playlist.title}</h3>
        {playlist.description ? <p className="playlist-card__desc clamp-2">{playlist.description}</p> : null}
        <div className="playlist-card__author">
          <Avatar name={playlist.owner.displayName} src={playlist.owner.avatarUrl} size="sm" />
          <span className="clamp-1">{playlist.owner.displayName}</span>
        </div>
      </div>
    </Link>
  );
}
